import * as React from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import rehypeKatex from 'rehype-katex'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import 'katex/dist/katex.min.css'
import { cn } from '../../lib/utils'
import { CodeBlock, InlineCode } from './CodeBlock'
import { MarkdownDiffBlock } from './MarkdownDiffBlock'
import { MarkdownJsonBlock } from './MarkdownJsonBlock'
import { MarkdownMermaidBlock } from './MarkdownMermaidBlock'
import { MarkdownDatatableBlock } from './MarkdownDatatableBlock'
import { MarkdownSpreadsheetBlock } from './MarkdownSpreadsheetBlock'
import { MarkdownExcalidrawBlock } from './MarkdownExcalidrawBlock'
import { MarkdownHtmlBlock } from './MarkdownHtmlBlock'
import { MarkdownPdfBlock } from './MarkdownPdfBlock'
import { MarkdownLatexBlock } from './MarkdownLatexBlock'
import { preprocessLinks } from './linkify'
import remarkCollapsibleSections from './remarkCollapsibleSections'
import { CollapsibleSection } from './CollapsibleSection'
import { useCollapsibleMarkdown } from './CollapsibleMarkdownContext'
import { wrapWithSafeProxy } from './safe-components'
import { MARKDOWN_MATH_OPTIONS } from './math-options'

/**
 * Render modes for markdown content:
 *
 * - 'terminal': Raw output with minimal formatting, control chars visible
 *   Best for: Debug output, raw logs, when you want to see exactly what's there
 *
 * - 'minimal': Clean rendering with syntax highlighting but no extra chrome
 *   Best for: Chat messages, inline content, when you want readability without clutter
 *
 * - 'full': Rich rendering with beautiful tables, styled code blocks, proper typography
 *   Best for: Documentation, long-form content, when presentation matters
 */
export type RenderMode = 'terminal' | 'minimal' | 'full'

export interface MarkdownProps {
  children: string
  /**
   * Render mode controlling formatting level
   * @default 'minimal'
   */
  mode?: RenderMode
  className?: string
  /**
   * Message ID for memoization (optional)
   * When provided, memoizes parsed blocks to avoid re-parsing during streaming
   */
  id?: string
  /**
   * Callback when a URL is clicked
   */
  onUrlClick?: (url: string) => void
  /**
   * Callback when a file path is clicked
   */
  onFileClick?: (path: string) => void
  /**
   * Enable collapsible headings
   * Requires wrapping in CollapsibleMarkdownProvider
   * @default false
   */
  collapsible?: boolean
  /**
   * Hide expand button on first mermaid block (when message starts with mermaid)
   * Used in chat to avoid overlap with TurnCard's fullscreen button
   * @default true
   */
  hideFirstMermaidExpand?: boolean
  /**
   * Hide expand button on first Excalidraw block (when message starts with excalidraw)
   * Used in chat to avoid overlap with TurnCard's fullscreen button
   * @default true
   */
  hideFirstExcalidrawExpand?: boolean
}

/** Context for collapsible sections */
interface CollapsibleContext {
  collapsedSections: Set<string>
  toggleSection: (id: string) => void
}

const FILE_EXTENSIONS = new Set([
  'ts', 'tsx', 'js', 'jsx', 'mjs', 'cjs',
  'md', 'markdown', 'mdx',
  'json', 'jsonc', 'yaml', 'yml',
  'py', 'go', 'rs', 'rb', 'php', 'swift', 'kt', 'java',
  'c', 'cpp', 'h', 'hpp',
  'css', 'scss', 'less',
  'html', 'htm', 'xml',
  'toml', 'ini', 'cfg', 'conf', 'env',
  'sql', 'graphql', 'vue', 'svelte', 'astro', 'prisma',
  'txt', 'log',
  'sh', 'bash', 'zsh',
  'mermaid',
  'excalidraw',
  // Image formats — routed to ImagePreviewOverlay via useLinkInterceptor
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico', 'avif',
  // PDF — routed to PDFPreviewOverlay via useLinkInterceptor
  'pdf',
])

function getPathWithoutQueryOrHash(value: string): string {
  const questionMark = value.indexOf('?')
  const hash = value.indexOf('#')
  const cutAt =
    questionMark === -1
      ? hash
      : hash === -1
        ? questionMark
        : Math.min(questionMark, hash)
  return cutAt === -1 ? value : value.slice(0, cutAt)
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function getExtension(path: string): string {
  const normalized = path.replace(/\\/g, '/')
  const fileName = normalized.split('/').pop() ?? normalized
  const dotIndex = fileName.lastIndexOf('.')
  if (dotIndex <= 0) return ''
  return fileName.slice(dotIndex + 1).toLowerCase()
}

function isLikelyFilePath(href: string): boolean {
  const decoded = safeDecode(href.trim())
  if (!decoded) return false

  const lower = decoded.toLowerCase()
  if (
    lower.startsWith('http://') ||
    lower.startsWith('https://') ||
    lower.startsWith('mailto:') ||
    lower.startsWith('agentoperator:')
  ) {
    return false
  }

  const pathCandidate = getPathWithoutQueryOrHash(decoded.replace(/^file:\/\//i, ''))
  const ext = getExtension(pathCandidate)
  if (!ext || !FILE_EXTENSIONS.has(ext)) return false

  if (/^(\/|~\/|\.\/|\.\.\/|[a-zA-Z]:[\\/])/.test(pathCandidate)) return true

  // Bare filename links (e.g. "diagram.excalidraw")
  return !decoded.includes('://')
}

function normalizeHrefToPath(href: string): string {
  const decoded = safeDecode(href.trim())
  if (!decoded.toLowerCase().startsWith('file://')) return decoded

  try {
    const parsed = new URL(decoded)
    const pathname = safeDecode(parsed.pathname)
    // Windows file URL: /C:/path -> C:/path
    return pathname.replace(/^\/([a-zA-Z]:[\\/])/, '$1')
  } catch {
    return decoded.replace(/^file:\/\//i, '')
  }
}

type ElectronOpenUrl = (url: string) => Promise<void>

function getElectronOpenUrl(): ElectronOpenUrl | null {
  if (typeof window === 'undefined') return null
  const electronAPI = (window as typeof window & {
    electronAPI?: {
      openUrl?: ElectronOpenUrl
    }
  }).electronAPI

  if (typeof electronAPI?.openUrl !== 'function') return null
  return electronAPI.openUrl
}

function openUrlFallback(href: string): boolean {
  const electronOpenUrl = getElectronOpenUrl()
  if (electronOpenUrl) {
    void electronOpenUrl(href)
    return true
  }

  if (typeof window === 'undefined') return false

  const opened = window.open(href, '_blank', 'noopener,noreferrer')
  if (opened) {
    opened.opener = null
    return true
  }

  return false
}

/**
 * Create custom components based on render mode.
 *
 * @param firstMermaidCodeRef - Ref holding the code of the first mermaid block
 *   when the markdown message starts with a mermaid fence. Used to hide the
 *   inline expand button on that block (TurnCard's own fullscreen button
 *   occupies the same top-right position). A ref is used so the closure can
 *   read the latest value without adding content to the memo deps — that would
 *   cause component re-mounting on every streaming update.
 * @param hideFirstMermaidExpand - Whether to hide the expand button on the first
 *   mermaid block when the message starts with a mermaid fence. Defaults to true.
 * @param firstExcalidrawCodeRef - Ref holding the code of the first excalidraw block
 *   when the markdown message starts with an excalidraw fence. Used to hide the
 *   inline expand button on that block.
 * @param hideFirstExcalidrawExpand - Whether to hide the expand button on the first
 *   excalidraw block when the message starts with an excalidraw fence. Defaults to true.
 */
function createComponents(
  mode: RenderMode,
  onUrlClick?: (url: string) => void,
  onFileClick?: (path: string) => void,
  collapsibleContext?: CollapsibleContext | null,
  firstMermaidCodeRef?: React.RefObject<string | null>,
  hideFirstMermaidExpand: boolean = true,
  firstExcalidrawCodeRef?: React.RefObject<string | null>,
  hideFirstExcalidrawExpand: boolean = true
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Components & Record<string, any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const baseComponents: Components & Record<string, any> = {
    // Section wrapper for collapsible headings
    div: ({ node, children, ...props }) => {
      const sectionId = (props as Record<string, unknown>)['data-section-id'] as string | undefined
      const headingLevel = (props as Record<string, unknown>)['data-heading-level'] as number | undefined

      // If this is a collapsible section div and we have context
      if (sectionId && headingLevel && collapsibleContext) {
        return (
          <CollapsibleSection
            sectionId={sectionId}
            headingLevel={headingLevel}
            isCollapsed={collapsibleContext.collapsedSections.has(sectionId)}
            onToggle={collapsibleContext.toggleSection}
          >
            {children}
          </CollapsibleSection>
        )
      }

      // Regular div
      return <div {...props}>{children}</div>
    },
    // Links: Make clickable with callbacks
    a: ({ href, children }) => {
      const handleClick = (e: React.MouseEvent) => {
        if (!href) return

        const isFilePath = isLikelyFilePath(href)
        const normalizedFilePath = isFilePath ? normalizeHrefToPath(href) : null

        // File links: prefer dedicated file handler, then URL handler.
        if (normalizedFilePath) {
          e.preventDefault()
          if (onFileClick) {
            onFileClick(normalizedFilePath)
            return
          }
          if (onUrlClick) {
            onUrlClick(normalizedFilePath)
            return
          }
          if (openUrlFallback(normalizedFilePath)) {
            return
          }
          return
        }

        // Regular URLs
        if (onUrlClick) {
          e.preventDefault()
          onUrlClick(href)
          return
        }

        if (openUrlFallback(href)) {
          e.preventDefault()
        }
      }

      return (
        <a
          href={href}
          onClick={handleClick}
          className="text-foreground hover:underline cursor-pointer"
        >
          {children}
        </a>
      )
    },
  }

  // Terminal mode: minimal formatting
  if (mode === 'terminal') {
    return {
      ...baseComponents,
      // No special code handling - just monospace
      code: ({ children }) => (
        <code className="font-mono">{children}</code>
      ),
      pre: ({ children }) => (
        <pre className="font-mono whitespace-pre-wrap my-2">{children}</pre>
      ),
      // Minimal paragraph spacing
      p: ({ children }) => <p className="my-1">{children}</p>,
      // Simple lists
      ul: ({ children }) => <ul className="list-disc list-inside my-1">{children}</ul>,
      ol: ({ children }) => <ol className="list-decimal list-inside my-1">{children}</ol>,
      li: ({ children }) => <li className="my-0.5">{children}</li>,
      // Plain tables
      table: ({ children }) => (
        <table className="my-2 font-mono text-sm">{children}</table>
      ),
      th: ({ children }) => <th className="text-left pr-4">{children}</th>,
      td: ({ children }) => <td className="pr-4">{children}</td>,
    }
  }

  // Minimal mode: clean with syntax highlighting
  if (mode === 'minimal') {
    return {
      ...baseComponents,
      // Inline code
      code: ({ className, children, ...props }) => {
        const match = /language-([\w-]+)/.exec(className || '')
        const isBlock = 'node' in props && props.node?.position?.start.line !== props.node?.position?.end.line

        // Block code
        if (match || isBlock) {
          const code = String(children).replace(/\n$/, '')
          const language = match?.[1]
          const lower = language?.toLowerCase()
          // Diff code blocks → pierre/diffs for a proper diff viewer
          if (lower === 'diff') {
            return <MarkdownDiffBlock code={code} className="my-1" />
          }
          // JSON code blocks → interactive tree viewer
          if (lower === 'json') {
            return <MarkdownJsonBlock code={code} className="my-1" />
          }
          // Datatable code blocks → sortable/filterable data table
          if (lower === 'datatable') {
            return <MarkdownDatatableBlock code={code} className="my-1" />
          }
          // Spreadsheet code blocks → Excel-style grid
          if (lower === 'spreadsheet') {
            return <MarkdownSpreadsheetBlock code={code} className="my-1" />
          }
          // HTML preview blocks → sandboxed iframe
          if (lower === 'html-preview') {
            return <MarkdownHtmlBlock code={code} className="my-1" />
          }
          // PDF preview blocks → inline first-page preview with fullscreen
          if (lower === 'pdf-preview') {
            return <MarkdownPdfBlock code={code} className="my-1" />
          }
          // LaTeX/math code blocks → KaTeX rendered display math
          if (lower === 'latex' || lower === 'math') {
            return <MarkdownLatexBlock code={code} className="my-1" />
          }
          // Mermaid code blocks → zinc-styled SVG diagram.
          // Hide the inline expand button when the mermaid block is the first
          // content in the message — TurnCard's own fullscreen button occupies
          // the same top-right spot. Detection uses firstMermaidCodeRef (content
          // match) rather than AST line positions which are unreliable after
          // preprocessLinks transforms the markdown.
          if (lower === 'mermaid') {
            const isFirstBlock = hideFirstMermaidExpand &&
                                firstMermaidCodeRef?.current != null &&
                                code === firstMermaidCodeRef.current
            return <MarkdownMermaidBlock code={code} className="my-1" showExpandButton={!isFirstBlock} />
          }
          // Excalidraw code blocks → SVG diagram preview.
          if (lower === 'excalidraw') {
            const isFirstBlock = hideFirstExcalidrawExpand &&
                                firstExcalidrawCodeRef?.current != null &&
                                code === firstExcalidrawCodeRef.current
            return <MarkdownExcalidrawBlock code={code} className="my-1" showExpandButton={!isFirstBlock} />
          }
          return <CodeBlock code={code} language={language} mode="full" className="my-1" />
        }

        // Inline code
        return <InlineCode>{children}</InlineCode>
      },
      pre: ({ children }) => <>{children}</>,
      // Comfortable paragraph spacing
      p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,
      // Styled lists - ul uses tighter spacing, ol uses standard for number alignment
      ul: ({ children }) => (
        <ul className="my-2 space-y-1 ps-[16px] pe-2 list-disc marker:text-[var(--md-bullets)]">
          {children}
        </ul>
      ),
      ol: ({ children }) => (
        <ol className="my-2 space-y-1 pl-6 list-decimal">{children}</ol>
      ),
      li: ({ children }) => <li>{children}</li>,
      // Clean tables
      table: ({ children }) => (
        <div className="my-3 overflow-x-auto">
          <table className="min-w-full text-sm">{children}</table>
        </div>
      ),
      thead: ({ children }) => <thead className="border-b">{children}</thead>,
      th: ({ children }) => (
        <th className="text-left py-2 px-3 font-semibold text-muted-foreground">{children}</th>
      ),
      td: ({ children }) => (
        <td className="py-2 px-3 border-b border-border/50">{children}</td>
      ),
      // Headings - H1/H2 same size, differentiated by weight
      h1: ({ children }) => <h1 className="font-sans text-[16px] font-bold mt-5 mb-3">{children}</h1>,
      h2: ({ children }) => <h2 className="font-sans text-[16px] font-semibold mt-4 mb-3">{children}</h2>,
      h3: ({ children }) => <h3 className="font-sans text-[15px] font-semibold mt-4 mb-2">{children}</h3>,
      // Blockquotes
      blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-muted-foreground/30 pl-3 my-2 text-muted-foreground italic">
          {children}
        </blockquote>
      ),
      // Horizontal rules
      hr: () => <hr className="my-4 border-border" />,
      // Strong/emphasis
      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
      em: ({ children }) => <em className="italic">{children}</em>,
    }
  }

  // Full mode: rich styling
  return {
    ...baseComponents,
    // Full code blocks with copy button
    code: ({ className, children, ...props }) => {
      const match = /language-([\w-]+)/.exec(className || '')
      const isBlock = 'node' in props && props.node?.position?.start.line !== props.node?.position?.end.line

      if (match || isBlock) {
        const code = String(children).replace(/\n$/, '')
        const language = match?.[1]
        const lower = language?.toLowerCase()
        // Diff code blocks → pierre/diffs for a proper diff viewer
        if (lower === 'diff') {
          return <MarkdownDiffBlock code={code} className="my-1" />
        }
        // JSON code blocks → interactive tree viewer
        if (lower === 'json') {
          return <MarkdownJsonBlock code={code} className="my-1" />
        }
        // Datatable code blocks → sortable/filterable data table
        if (lower === 'datatable') {
          return <MarkdownDatatableBlock code={code} className="my-1" />
        }
        // Spreadsheet code blocks → Excel-style grid
        if (lower === 'spreadsheet') {
          return <MarkdownSpreadsheetBlock code={code} className="my-1" />
        }
        // HTML preview blocks → sandboxed iframe
        if (lower === 'html-preview') {
          return <MarkdownHtmlBlock code={code} className="my-1" />
        }
        // PDF preview blocks → inline first-page preview with fullscreen
        if (lower === 'pdf-preview') {
          return <MarkdownPdfBlock code={code} className="my-1" />
        }
        // Mermaid code blocks → zinc-styled SVG diagram.
        // (Same first-block detection as minimal mode — see comment above.)
        if (lower === 'mermaid') {
          const isFirstBlock = hideFirstMermaidExpand &&
                              firstMermaidCodeRef?.current != null &&
                              code === firstMermaidCodeRef.current
          return <MarkdownMermaidBlock code={code} className="my-1" showExpandButton={!isFirstBlock} />
        }
        // Excalidraw code blocks → SVG diagram preview.
        if (lower === 'excalidraw') {
          const isFirstBlock = hideFirstExcalidrawExpand &&
                              firstExcalidrawCodeRef?.current != null &&
                              code === firstExcalidrawCodeRef.current
          return <MarkdownExcalidrawBlock code={code} className="my-1" showExpandButton={!isFirstBlock} />
        }
        return <CodeBlock code={code} language={language} mode="full" className="my-1" />
      }

      return <InlineCode>{children}</InlineCode>
    },
    pre: ({ children }) => <>{children}</>,
    // Rich paragraph spacing
    p: ({ children }) => <p className="my-3 leading-relaxed">{children}</p>,
    // Styled lists - ul uses tighter spacing, ol uses standard for number alignment
    ul: ({ children }) => (
      <ul className="my-3 space-y-1.5 ps-[16px] pe-2 list-disc marker:text-[var(--md-bullets)]">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="my-3 space-y-1.5 pl-6 list-decimal">{children}</ol>
    ),
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    // Beautiful tables
    table: ({ children }) => (
      <div className="my-4 overflow-x-auto rounded-md border">
        <table className="min-w-full divide-y divide-border">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-muted/50">{children}</thead>,
    tbody: ({ children }) => <tbody className="divide-y divide-border">{children}</tbody>,
    th: ({ children }) => (
      <th className="text-left py-3 px-4 font-semibold text-sm">{children}</th>
    ),
    td: ({ children }) => (
      <td className="py-3 px-4 text-sm">{children}</td>
    ),
    tr: ({ children }) => (
      <tr className="hover:bg-muted/30 transition-colors">{children}</tr>
    ),
    // Rich headings - H1/H2 same size, differentiated by weight
    h1: ({ children }) => (
      <h1 className="font-sans text-[16px] font-bold mt-7 mb-4">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="font-sans text-[16px] font-semibold mt-6 mb-3">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="font-sans text-[15px] font-semibold mt-5 mb-3">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-[14px] font-semibold mt-3 mb-1">{children}</h4>
    ),
    // Styled blockquotes
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-foreground/30 bg-muted/30 pl-4 pr-3 py-2 my-3 rounded-r-md">
        {children}
      </blockquote>
    ),
    // Task lists (GFM)
    input: ({ type, checked }) => {
      if (type === 'checkbox') {
        return (
          <input
            type="checkbox"
            checked={checked}
            readOnly
            className="mr-2 rounded border-muted-foreground"
          />
        )
      }
      return <input type={type} />
    },
    // Horizontal rules
    hr: () => <hr className="my-6 border-border" />,
    // Strong/emphasis
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    del: ({ children }) => <del className="line-through text-muted-foreground">{children}</del>,
    // Handle unknown <markdown> tags that may come through rehype-raw
    markdown: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
    // Suppress internal XML tags from edit_request blocks and other system instructions.
    // These are injected by the app (e.g., EditPopover automation config flow) and should
    // not render as DOM elements. rehype-raw passes them through, causing React warnings.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    edit_request: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    file: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    context: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  }
}

/**
 * Markdown - Customizable markdown renderer with multiple render modes
 *
 * Features:
 * - Three render modes: terminal, minimal, full
 * - Syntax highlighting via Shiki
 * - GFM support (tables, task lists, strikethrough)
 * - Clickable links and file paths
 * - Memoization for streaming performance
 */
export function Markdown({
  children,
  mode = 'minimal',
  className,
  id,
  onUrlClick,
  onFileClick,
  collapsible = false,
  hideFirstMermaidExpand = true,
  hideFirstExcalidrawExpand = true,
}: MarkdownProps) {
  // Get collapsible context if enabled
  const collapsibleContext = useCollapsibleMarkdown()

  // Extract the first mermaid code block's content when the message starts
  // with a mermaid fence. Stored in a ref so createComponents can read it
  // without adding `children` to the memo deps (which would remount all
  // components on every streaming update, breaking internal state).
  const firstMermaidCodeRef = React.useRef<string | null>(null)
  const trimmed = children.trimStart()
  if (trimmed.startsWith('```mermaid')) {
    const m = trimmed.match(/^```mermaid\n([\s\S]*?)```/)
    firstMermaidCodeRef.current = m?.[1] ? m[1].replace(/\n$/, '') : null
  } else {
    firstMermaidCodeRef.current = null
  }

  // Extract the first excalidraw code block's content when the message starts
  // with an excalidraw fence (same rationale as mermaid above).
  const firstExcalidrawCodeRef = React.useRef<string | null>(null)
  if (trimmed.startsWith('```excalidraw')) {
    const m = trimmed.match(/^```excalidraw\n([\s\S]*?)```/)
    firstExcalidrawCodeRef.current = m?.[1] ? m[1].replace(/\n$/, '') : null
  } else {
    firstExcalidrawCodeRef.current = null
  }

  const components = React.useMemo(
    () => wrapWithSafeProxy(createComponents(
      mode,
      onUrlClick,
      onFileClick,
      collapsible ? collapsibleContext : null,
      firstMermaidCodeRef,
      hideFirstMermaidExpand,
      firstExcalidrawCodeRef,
      hideFirstExcalidrawExpand
    )),
    [
      mode,
      onUrlClick,
      onFileClick,
      collapsible,
      collapsibleContext,
      hideFirstMermaidExpand,
      hideFirstExcalidrawExpand,
    ]
  )

  // Preprocess to convert raw URLs and file paths to markdown links
  const processedContent = React.useMemo(
    () => preprocessLinks(children),
    [children]
  )

  // Conditionally include the collapsible sections plugin
  const remarkPlugins = React.useMemo(
    () => {
      const mathPlugin: [typeof remarkMath, typeof MARKDOWN_MATH_OPTIONS] = [
        remarkMath,
        MARKDOWN_MATH_OPTIONS,
      ]
      return collapsible
        ? [remarkGfm, mathPlugin, remarkCollapsibleSections]
        : [remarkGfm, mathPlugin]
    },
    [collapsible]
  )

  return (
    <div className={cn('markdown-content', className)}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={[rehypeKatex, rehypeRaw]}
        components={components}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  )
}

/**
 * MemoizedMarkdown - Optimized for streaming scenarios
 *
 * Splits content into blocks and memoizes each block separately,
 * so only new/changed blocks re-render during streaming.
 */
export const MemoizedMarkdown = React.memo(
  Markdown,
  (prevProps, nextProps) => {
    // If id is provided, use it for memoization
    if (prevProps.id && nextProps.id) {
      return (
        prevProps.id === nextProps.id &&
        prevProps.children === nextProps.children &&
        prevProps.mode === nextProps.mode
      )
    }
    // Otherwise compare content and mode
    return (
      prevProps.children === nextProps.children &&
      prevProps.mode === nextProps.mode
    )
  }
)
MemoizedMarkdown.displayName = 'MemoizedMarkdown'

// Re-export for convenience
export { CodeBlock, InlineCode } from './CodeBlock'
export { CollapsibleMarkdownProvider } from './CollapsibleMarkdownContext'
